<template>
  <div class="pickup-edit-container">
    <!-- Enhanced Header -->
    <b-card class="mb-4 brand-header-card">
      <template #header>
        <div class="d-flex align-items-center justify-content-between">
          <div>
            <h4 class="brand-page-title mb-1">
              <feather-icon
                icon="EditIcon"
                class="mr-2 text-primary"
              />
              Modifier la collecte #{{ pickupId }}
            </h4>
            <p class="brand-page-subtitle mb-0">
              Gérez les expéditions, le statut et les détails de la collecte
            </p>
          </div>
          <b-badge
            v-if="pickup"
            :variant="getStatusVariant(pickup.status)"
            class="brand-badge-large"
          >
            {{ pickup.status_display }}
          </b-badge>
        </div>
      </template>
    </b-card>

    <!-- Loading State -->
    <div
      v-if="isLoading"
      class="text-center mb-4"
    >
      <b-spinner
        variant="primary"
        class="mr-2"
      />
      <span>Chargement du pickup...</span>
    </div>

    <!-- Error State -->
    <b-alert
      :show="error !== null"
      variant="danger"
    >
      <h4 class="alert-heading">
        Erreur
      </h4>
      <div class="alert-body">
        {{ error }}
      </div>
    </b-alert>

    <!-- Enhanced status restriction message -->
    <b-alert
      v-if="pickup && pickup.status === 'confirmed'"
      variant="warning"
      show
      class="mb-4"
    >
      <h6 class="alert-heading">
        Collecte confirmée
      </h6>
      <div class="alert-body">
        Cette collecte a été confirmée et ne peut plus être modifiée. Seuls les gestionnaires peuvent maintenant modifier cette collecte.
      </div>
    </b-alert>

    <!-- Edit Form -->
    <div v-if="pickup && !isLoading">
      <!-- Pickup Details Card -->
      <b-card class="mb-4 brand-card">
        <template #header>
          <h6 class="mb-0 brand-section-title">
            <feather-icon
              icon="InfoIcon"
              class="mr-2"
            />
            Détails de la collecte
          </h6>
        </template>

        <validation-observer
          ref="pickupForm"
          v-slot="{ handleSubmit }"
        >
          <b-form @submit.prevent="handleSubmit(updatePickup)">
            <b-row>
              <b-col md="6">
                <b-form-group
                  label="Statut de la collecte"
                  label-for="status"
                  class="brand-form-group"
                >
                  <validation-provider
                    v-slot="{ errors }"
                    name="status"
                    rules="required"
                  >
                    <v-select
                      v-model="form.status"
                      :clearable="false"
                      placeholder="Sélectionner le statut de la collecte"
                      label="text"
                      :options="allowedStatusOptions"
                      class="brand-select"
                      :disabled="!canChangeStatus"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                    <small
                      v-if="pickup.status === 'confirmed'"
                      class="text-muted d-block mt-1"
                    >
                      Statut confirmé - modification restreinte pour la collecte
                      <!-- Save/Update Button with Tooltip -->
                      <div class="d-flex justify-content-end mt-3">
                        <span id="edit-pickup-btn-wrapper">
                          <b-button
                            variant="primary"
                            class="brand-btn-primary"
                            :disabled="currentOrders.length === 0 || isSubmitting"
                            @click="updatePickup"
                          >
                            <feather-icon
                              icon="SaveIcon"
                              class="mr-1"
                            />
                            {{ isSubmitting ? 'Enregistrement...' : 'Enregistrer les modifications' }}
                          </b-button>
                          <b-tooltip
                            v-if="currentOrders.length === 0"
                            target="edit-pickup-btn-wrapper"
                            triggers="hover focus"
                            placement="top"
                          >
                            Ajoutez au moins une commande à la collecte avant d'enregistrer.
                          </b-tooltip>
                        </span>
                      </div>
                    </small>
                  </validation-provider>
                </b-form-group>
              </b-col>

              <b-col md="6">
                <b-form-group
                  label="Date et heure de la collecte (optionnel)"
                  label-for="pickup_datetime"
                  class="brand-form-group"
                >
                  <b-form-input
                    v-model="form.pickupDatetime"
                    type="datetime-local"
                    class="brand-input brand-datetime-input"
                    :disabled="!pickup.can_be_modified"
                    placeholder="Planifier la date et l’heure de la collecte"
                  />
                  <small class="text-muted d-block mt-1">
                    Format : JJ/MM/AAAA HH:MM
                  </small>
                </b-form-group>
              </b-col>

              <b-col cols="12">
                <b-form-group
                  label="Notes additionnelles (optionnel)"
                  label-for="notes"
                  class="brand-form-group"
                >
                  <b-form-textarea
                    v-model="form.notes"
                    placeholder="Ajoutez des notes ou instructions pour la collecte..."
                    rows="3"
                    class="brand-input"
                    :disabled="!pickup.can_be_modified"
                  />
                </b-form-group>
              </b-col>

              <b-col md="6">
                <b-form-group
                  label="Montant total (TTC)"
                  class="brand-form-group"
                >
                  <b-form-input
                    :value="formatPrice(pickup.total_price_ttc)"
                    class="brand-input"
                    readonly
                  />
                </b-form-group>
              </b-col>

              <b-col md="6">
                <b-form-group
                  label="Total livraison (TND)"
                  class="brand-form-group"
                >
                  <b-form-input
                    :value="formatPrice(pickup.total_price_delivery)"
                    class="brand-input"
                    readonly
                  />
                </b-form-group>
              </b-col>
            </b-row>

            <!-- Action Buttons -->
            <div class="d-flex justify-content-between mt-4">
              <div>
                <b-button
                  variant="primary"
                  type="submit"
                  class="brand-btn-primary mr-2"
                  :disabled="isSubmitting || (!pickup.can_be_modified && pickup.status === 'confirmed')"
                >
                  <b-spinner
                    v-if="isSubmitting"
                    small
                    class="mr-1"
                  />
                  <feather-icon
                    v-else
                    icon="SaveIcon"
                    class="mr-1"
                    size="16"
                  />
                  {{ isSubmitting ? 'Sauvegarde...' : 'Sauvegarder la collecte' }}
                </b-button>
                <b-button
                  variant="outline-secondary"
                  class="brand-btn-secondary"
                  :disabled="isSubmitting"
                  @click="resetForm"
                >
                  <feather-icon
                    icon="RefreshCwIcon"
                    class="mr-1"
                    size="16"
                  />
                  Réinitialiser
                </b-button>
              </div>
              <b-button
                variant="outline-primary"
                :to="{ name: 'pickup-show', params: { id: pickupId } }"
              >
                <feather-icon
                  icon="EyeIcon"
                  class="mr-1"
                  size="16"
                />
                Voir la collecte
              </b-button>
            </div>
          </b-form>
        </validation-observer>
      </b-card>

      <!-- Orders Management Card -->
      <b-card class="mb-4 brand-card">
        <template #header>
          <div class="d-flex justify-content-between align-items-center">
            <h6 class="mb-0 brand-section-title">
              <feather-icon
                icon="PackageIcon"
                class="mr-2"
              />
              Gestion des expéditions ({{ selectedOrderIds.length }})
            </h6>
            <div>
              <b-button
                v-if="pickup.can_be_modified"
                variant="outline-primary"
                size="sm"
                class="mr-2"
                :disabled="isLoadingOrders"
                @click="loadAvailableOrders"
              >
                <feather-icon
                  icon="PlusIcon"
                  size="14"
                  class="mr-1"
                />
                Ajouter des expéditions
              </b-button>
              <b-button
                variant="outline-secondary"
                size="sm"
                :disabled="isLoadingOrders"
                @click="refreshOrders"
              >
                <feather-icon
                  icon="RefreshCwIcon"
                  size="14"
                />
              </b-button>
            </div>
          </div>
        </template>

        <!-- Current Orders Table -->
        <div v-if="currentOrders.length > 0">
          <h6 class="text-muted mb-3">
            Expéditions actuelles
          </h6>
          <vue-good-table
            :columns="orderColumns"
            :rows="currentOrders"
            :search-options="{ enabled: true }"
            :pagination-options="{ enabled: false }"
            :select-options="{
              enabled: pickup.can_be_modified,
              selectOnCheckboxOnly: true,
              selectionText: 'commandes sélectionnées',
              clearSelectionText: 'Tout désélectionner',
            }"
            @on-selected-rows-change="onOrderSelectionChanged"
          >
            <template
              slot="table-row"
              slot-scope="props"
            >
              <!-- Order ID -->
              <div
                v-if="props.column.field === 'id'"
                class="font-weight-bold text-primary"
              >
                #{{ props.row.id }}
              </div>

              <!-- Client -->
              <div
                v-else-if="props.column.field === 'name'"
                class="text-nowrap"
              >
                <div class="font-weight-medium">
                  {{ props.row.name }}
                </div>
                <small class="text-muted">{{ props.row.phone1 }}</small>
              </div>

              <!-- Governorate -->
              <div
                v-else-if="props.column.field === 'governorate'"
                class="text-nowrap"
              >
                <feather-icon
                  icon="MapPinIcon"
                  size="14"
                  class="mr-1 text-muted"
                />
                {{ props.row.governorate }}
              </div>

              <!-- Price -->
              <div
                v-else-if="props.column.field === 'price_ttc'"
                class="text-right font-weight-bold text-success"
              >
                {{ formatPrice(props.row.price_ttc) }} TND
              </div>

              <!-- Actions -->
              <div
                v-else-if="props.column.field === 'actions'"
                class="text-center"
              >
                <b-button
                  v-if="pickup.can_be_modified"
                  variant="outline-danger"
                  size="sm"
                  aria-label="Retirer l’expédition de la collecte"
                  @click="removeOrder(props.row.id)"
                >
                  <feather-icon
                    icon="XIcon"
                    size="14"
                  />
                </b-button>
              </div>

              <!-- Default -->
              <span v-else>
                {{ props.formattedRow[props.column.field] }}
              </span>
            </template>
          </vue-good-table>

          <!-- Bulk Actions -->
          <div
            v-if="selectedOrders.length > 0 && pickup.can_be_modified"
            class="mt-3 p-3 bg-light rounded"
          >
            <div class="d-flex justify-content-between align-items-center">
              <span>
                <strong>{{ selectedOrders.length }}</strong> expédition(s) sélectionnée(s)
              </span>
              <b-button
                variant="danger"
                size="sm"
                aria-label="Retirer les expéditions sélectionnées de la collecte"
                @click="removeSelectedOrders"
              >
                <feather-icon
                  icon="TrashIcon"
                  size="14"
                  class="mr-1"
                />
                Retirer la sélection
              </b-button>
            </div>
          </div>
        </div>

        <div
          v-else
          class="text-center py-4"
        >
          <feather-icon
            icon="PackageIcon"
            size="48"
            class="text-muted mb-3"
          />
          <h6 class="text-muted">
            Aucune expédition dans cette collecte
          </h6>
          <p class="text-muted mb-3">
            Ajoutez des commandes pour commencer
          </p>
          <b-button
            v-if="pickup.can_be_modified"
            variant="primary"
            @click="loadAvailableOrders"
          >
            <feather-icon
              icon="PlusIcon"
              size="16"
              class="mr-1"
            />
            Ajouter des commandes
          </b-button>
        </div>
      </b-card>
    </div>

    <!-- Add Orders Modal -->
    <b-modal
      v-model="showAddOrdersModal"
      title="Ajouter des commandes"
      size="xl"
      @hidden="onModalHidden"
    >
      <div v-if="isLoadingOrders">
        <div class="text-center py-4">
          <b-spinner
            variant="primary"
            class="mr-2"
          />
          <span>Chargement des commandes disponibles...</span>
        </div>
      </div>

      <div v-else-if="availableOrders.length === 0">
        <div class="text-center py-4">
          <feather-icon
            icon="PackageIcon"
            size="48"
            class="text-muted mb-3"
          />
          <h6 class="text-muted">
            Aucune commande disponible
          </h6>
          <p class="text-muted">
            Toutes vos commandes sont déjà assignées à des pickups.
          </p>
        </div>
      </div>

      <div v-else>
        <vue-good-table
          :columns="orderColumns"
          :rows="availableOrders"
          :search-options="{ enabled: true }"
          :pagination-options="{ enabled: true, perPage: 20 }"
          :select-options="{
            enabled: true,
            selectOnCheckboxOnly: true,
            selectionText: 'commandes sélectionnées',
            clearSelectionText: 'Tout désélectionner',
          }"
          @on-selected-rows-change="onAvailableOrderSelectionChanged"
        >
          <template
            slot="table-row"
            slot-scope="props"
          >
            <!-- Order ID -->
            <div
              v-if="props.column.field === 'id'"
              class="font-weight-bold text-primary"
            >
              #{{ props.row.id }}
            </div>

            <!-- Client -->
            <div
              v-else-if="props.column.field === 'name'"
              class="text-nowrap"
            >
              <div class="font-weight-medium">
                {{ props.row.name }}
              </div>
              <small class="text-muted">{{ props.row.phone1 }}</small>
            </div>

            <!-- Governorate -->
            <div
              v-else-if="props.column.field === 'governorate'"
              class="text-nowrap"
            >
              <feather-icon
                icon="MapPinIcon"
                size="14"
                class="mr-1 text-muted"
              />
              {{ props.row.governorate }}
            </div>

            <!-- Price -->
            <div
              v-else-if="props.column.field === 'price_ttc'"
              class="text-right font-weight-bold text-success"
            >
              {{ formatPrice(props.row.price_ttc) }} TND
            </div>

            <!-- Default -->
            <span v-else>
              {{ props.formattedRow[props.column.field] }}
            </span>
          </template>
        </vue-good-table>
      </div>

      <template #modal-footer>
        <div class="w-100 d-flex justify-content-between">
          <div>
            <span
              v-if="selectedAvailableOrders.length > 0"
              class="text-muted"
            >
              {{ selectedAvailableOrders.length }} commande(s) sélectionnée(s)
            </span>
          </div>
          <div>
            <b-button
              variant="outline-secondary"
              class="mr-2"
              @click="showAddOrdersModal = false"
            >
              Annuler
            </b-button>
            <b-button
              variant="primary"
              :disabled="selectedAvailableOrders.length === 0 || isSubmitting"
              @click="addSelectedOrders"
            >
              <b-spinner
                v-if="isSubmitting"
                small
                class="mr-1"
              />
              Ajouter {{ selectedAvailableOrders.length }} commande(s)
            </b-button>
          </div>
        </div>
      </template>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard, BFormGroup, BFormInput, BFormTextarea,
  BButton, BSpinner, BAlert, BBadge, BModal, BRow, BCol, BForm,
} from 'bootstrap-vue'
import { VueGoodTable } from 'vue-good-table'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import vSelect from 'vue-select'
import axios from 'axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { required } from '@validations'
import storeAuth from '@/store/store'

export default {
  name: 'EditPickup',
  components: {
    BCard,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BButton,
    BSpinner,
    BAlert,
    BBadge,
    BModal,
    BRow,
    BCol,
    BForm,
    VueGoodTable,
    ValidationProvider,
    ValidationObserver,
    vSelect,
  },
  data() {
    return {
      pickupId: null,
      pickup: null,
      currentUser: null, // Store current user properly
      currentOrders: [],
      availableOrders: [],
      selectedOrders: [],
      selectedAvailableOrders: [],
      selectedOrderIds: [],

      // Form data with single datetime field
      form: {
        status: null,
        notes: '',
        pickupDatetime: null,
      },

      // Status options for suppliers (restricted)
      supplierStatusOptions: [
        { value: 'pending', text: 'En attente' },
        { value: 'confirmed', text: 'Confirmé' },
      ],

      // Full status options for admin/managers
      allStatusOptions: [
        { value: 'pending', text: 'En attente' },
        { value: 'confirmed', text: 'Confirmé' },
        { value: 'in_transit', text: 'En transit' },
        { value: 'delivered', text: 'Livré' },
        { value: 'cancelled', text: 'Annulé' },
      ],

      // States
      isLoading: true,
      isLoadingOrders: false,
      isSubmitting: false,
      error: null,
      showAddOrdersModal: false,
      canEditPickup: false, // Permission check

      // Date picker configuration
      datePickerConfig: {
        locale: 'fr-FR',
        startWeekday: 1,
        labelHelp: 'Utilisez les flèches pour naviguer',
        labelPrevYear: 'Année précédente',
        labelPrevMonth: 'Mois précédent',
        labelCurrentMonth: 'Mois actuel',
        labelNextMonth: 'Mois suivant',
        labelNextYear: 'Année suivante',
        labelToday: 'Aujourd\'hui',
        labelSelected: 'Date sélectionnée',
        labelNoDateSelected: 'Aucune date sélectionnée',
        labelCalendar: 'Calendrier',
        labelNav: 'Navigation du calendrier',
      },

      // Table columns
      orderColumns: [
        {
          label: 'BL',
          field: 'id',
          width: '100px',
        },
        {
          label: 'Client',
          field: 'name',
          width: '200px',
        },
        {
          label: 'Gouvernorat',
          field: 'governorate',
          width: '150px',
        },
        {
          label: 'Prix TTC',
          field: 'price_ttc',
          width: '120px',
        },
        {
          label: 'Actions',
          field: 'actions',
          width: '100px',
          sortable: false,
        },
      ],

      required,
    }
  },

  computed: {
    allowedStatusOptions() {
      // If user is supplier
      if (this.currentUser?.role === 'supplier') {
        // If pickup is confirmed, don't allow any status change
        if (this.pickup?.status === 'confirmed') {
          return [{ value: 'confirmed', text: 'Confirmé' }]
        }
        // Otherwise allow pending and confirmed only
        return this.supplierStatusOptions
      }
      // Admin and managers can see all options
      return this.allStatusOptions
    },

    canChangeStatus() {
      if (!this.pickup || !this.currentUser) return false

      // Suppliers cannot change status if already confirmed
      if (this.currentUser.role === 'supplier' && this.pickup.status === 'confirmed') {
        return false
      }

      // Otherwise follow normal modification rules
      return this.pickup.can_be_modified || this.currentUser.role !== 'supplier'
    },
  },

  created() {
    this.pickupId = this.$route.params.id
    this.getCurrentUser()
    this.checkPermissions()
    this.getPickup()
  },

  methods: {
    // Get current user from store or localStorage
    getCurrentUser() {
      // Try to get user from store first
      this.currentUser = storeAuth.state.user

      // If not in store, get from localStorage
      if (!this.currentUser || !this.currentUser.id) {
        const userId = localStorage.getItem('user_id')
        const userRole = localStorage.getItem('role')
        const userName = localStorage.getItem('username')

        if (userId) {
          this.currentUser = {
            id: parseInt(userId, 10),
            role: userRole,
            username: userName,
          }
        }
      }

      // Ensure user ID is a number for proper comparison
      if (this.currentUser && this.currentUser.id) {
        this.currentUser.id = parseInt(this.currentUser.id, 10)
      }
    },

    // Permission checking method
    checkPermissions() {
      if (!this.currentUser) {
        this.error = 'Utilisateur non authentifié.'
        this.$router.push('/auth/login')
        return false
      }

      // Only suppliers can edit pickups
      if (this.currentUser.role !== 'supplier') {
        this.error = 'Seuls les fournisseurs peuvent modifier les pickups.'
        this.$router.push('/pickups')
        return false
      }

      this.canEditPickup = true
      return true
    },

    async getPickup() {
      if (!this.checkPermissions()) {
        return
      }

      this.isLoading = true
      this.error = null

      try {
        const { data } = await axios.get(`/api/pickups/detail/${this.pickupId}/`)

        // Check if current user is the owner of the pickup
        const pickupSupplierId = parseInt(data.supplier?.id || data.supplier, 10)
        const currentUserId = parseInt(this.currentUser.id, 10)

        if (pickupSupplierId !== currentUserId) {
          this.error = 'Vous ne pouvez modifier que vos propres pickups.'
          this.$router.push('/pickups')
          return
        }

        // Check if pickup status allows modification
        if (!data.can_be_modified) {
          this.error = 'Ce pickup ne peut plus être modifié car il a été confirmé ou livré.'
          // Don't redirect, just show read-only view
        }

        this.pickup = data
        this.currentOrders = data.orders || []
        this.selectedOrderIds = this.currentOrders.map(order => order.id)

        // Set form data with single datetime
        const statusOption = this.allowedStatusOptions.find(opt => opt.value === data.status)
        this.form.status = statusOption || null
        this.form.notes = data.notes || ''

        // Convert datetime for HTML datetime-local input
        if (data.pickup_datetime) {
          // Convert from ISO datetime to datetime-local format (YYYY-MM-DDTHH:MM)
          const pickupDate = new Date(data.pickup_datetime)
          const year = pickupDate.getFullYear()
          const month = String(pickupDate.getMonth() + 1).padStart(2, '0')
          const day = String(pickupDate.getDate()).padStart(2, '0')
          const hours = String(pickupDate.getHours()).padStart(2, '0')
          const minutes = String(pickupDate.getMinutes()).padStart(2, '0')

          this.form.pickupDatetime = `${year}-${month}-${day}T${hours}:${minutes}`
        } else {
          this.form.pickupDatetime = null
        }
      } catch (error) {
        console.error('Error loading pickup:', error)
        if (error.response?.status === 403) {
          this.error = 'Vous n\'avez pas les permissions pour accéder à ce pickup.'
          this.$router.push('/pickups')
        } else if (error.response?.status === 404) {
          this.error = 'Pickup introuvable.'
          this.$router.push('/pickups')
        } else {
          this.error = 'Erreur lors du chargement du pickup'
          this.showToast('danger', 'top-center', 'Erreur lors du chargement du pickup')
        }
      } finally {
        this.isLoading = false
      }
    },

    async loadAvailableOrders() {
      this.isLoadingOrders = true
      this.showAddOrdersModal = true

      try {
        const { data } = await axios.get(`/api/pickups/available-orders/${this.pickupId}/`)
        this.availableOrders = data
      } catch (error) {
        this.showToast('danger', 'top-center', 'Erreur lors du chargement des commandes disponibles')
        this.showAddOrdersModal = false
      } finally {
        this.isLoadingOrders = false
      }
    },

    async updatePickup() {
      // ...existing validation...

      this.isSubmitting = true

      try {
        const updateData = {
          status: this.form.status?.value || this.form.status,
          notes: this.form.notes,
          order_ids: this.selectedOrderIds,
        }

        // Convert datetime-local to ISO format for API
        if (this.form.pickupDatetime) {
          updateData.pickup_datetime = new Date(this.form.pickupDatetime).toISOString()
        } else {
          updateData.pickup_datetime = null
        }

        await axios.put(`/api/pickups/update/${this.pickupId}/`, updateData)

        this.showToast('success', 'top-center', 'Pickup modifié avec succès')

        // Refresh pickup data
        await this.getPickup()
      } catch (error) {
        let errorMessage = 'Erreur lors de la modification'
        if (error.response?.status === 403) {
          errorMessage = 'Vous n\'avez pas les permissions pour modifier ce pickup'
        } else if (error.response?.status === 400) {
          errorMessage = error.response.data?.detail || 'Données invalides'
        } else if (error.response?.data?.detail) {
          errorMessage = error.response.data.detail
        }
        this.showToast('danger', 'top-center', errorMessage)
      } finally {
        this.isSubmitting = false
      }
    },

    resetForm() {
      this.getPickup()
    },

    refreshOrders() {
      this.getPickup()
    },

    // Order management methods
    onOrderSelectionChanged(selectedRows) {
      this.selectedOrders = selectedRows.selectedRows
    },

    onAvailableOrderSelectionChanged(selectedRows) {
      this.selectedAvailableOrders = selectedRows.selectedRows
    },

    removeOrder(orderId) {
      this.selectedOrderIds = this.selectedOrderIds.filter(id => id !== orderId)
      this.currentOrders = this.currentOrders.filter(order => order.id !== orderId)
    },

    removeSelectedOrders() {
      const selectedIds = this.selectedOrders.map(order => order.id)
      this.selectedOrderIds = this.selectedOrderIds.filter(id => !selectedIds.includes(id))
      this.currentOrders = this.currentOrders.filter(order => !selectedIds.includes(order.id))
      this.selectedOrders = []
    },

    addSelectedOrders() {
      if (this.selectedAvailableOrders.length === 0) return

      // Add to selected order IDs
      const newOrderIds = this.selectedAvailableOrders.map(order => order.id)
      this.selectedOrderIds = [...this.selectedOrderIds, ...newOrderIds]

      // Add to current orders
      this.currentOrders = [...this.currentOrders, ...this.selectedAvailableOrders]

      // Remove from available orders
      const addedIds = new Set(newOrderIds)
      this.availableOrders = this.availableOrders.filter(order => !addedIds.has(order.id))

      this.showAddOrdersModal = false
      this.selectedAvailableOrders = []

      this.showToast('success', 'top-center', `${newOrderIds.length} commande(s) ajoutée(s)`)
    },

    onModalHidden() {
      this.selectedAvailableOrders = []
    },

    // Utility methods
    getStatusVariant(status) {
      const variants = {
        pending: 'warning',
        confirmed: 'info',
        in_transit: 'primary',
        delivered: 'success',
        cancelled: 'danger',
      }
      return variants[status] || 'secondary'
    },

    formatPrice(price) {
      return parseFloat(price || 0).toFixed(3)
    },

    showToast(variant, position, text) {
      this.$toast(
        {
          component: ToastificationContent,
          props: {
            title: 'Notification',
            icon: 'InfoIcon',
            text,
            variant,
          },
        },
        {
          position,
        },
      )
    },
  },
}
</script>

<style lang="scss">
@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap');
@import "@core/scss/vue/libs/vue-select.scss";
@import "@core/scss/vue/libs/vue-good-table.scss";

// Force immediate style application
html {
  font-family: 'Poppins', sans-serif;
}

// Enhanced Pickup Edit Container
.pickup-edit-container {
  font-family: 'Poppins', sans-serif;

  // Force immediate style application
  * {
    font-family: 'Poppins', sans-serif !important;
  }

  // Enhanced header styling
  .brand-header-card {
    border: none;
    box-shadow: 0 4px 25px 0 rgba(232, 0, 0, 0.1);
    border-radius: 16px;

    .card-header {
      background: linear-gradient(135deg, rgba(232, 0, 0, 0.05) 0%, rgba(29, 29, 27, 0.02) 100%);
      border-bottom: none;
      border-radius: 16px 16px 0 0 !important;
      padding: 1.5rem 2rem;
    }
  }

  .brand-page-title {
    color: $brand-black;
    font-weight: 700;
    font-size: 1.5rem;
    margin: 0;
  }

  .brand-page-subtitle {
    color: lighten($brand-black, 30%);
    font-weight: 400;
    font-size: 0.95rem;
  }

  // Enhanced card styling
  .brand-card {
    border: none;
    border-radius: 12px;
    box-shadow: 0 4px 25px 0 rgba(29, 29, 27, 0.1);

    .card-header {
      background: rgba(29, 29, 27, 0.02);
      border-bottom: 2px solid rgba(232, 0, 0, 0.1);
      border-radius: 12px 12px 0 0 !important;
      padding: 1rem 1.5rem;
    }

    .card-body {
      padding: 2rem;
    }
  }

  .brand-section-title {
    color: $brand-black;
    font-weight: 600;
    font-size: 1.1rem;
    margin: 0;
  }

  // Form styling
  .brand-form-group {
    margin-bottom: 1.5rem;

    label {
      color: $brand-black;
      font-weight: 500;
      font-size: 0.9rem;
      margin-bottom: 0.5rem;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      font-family: 'Poppins', sans-serif;
    }
  }

  .brand-input {
    border: 2px solid #e3e6f0;
    border-radius: 8px;
    padding: 0.75rem 1rem;
    font-size: 0.95rem;
    font-family: 'Poppins', sans-serif;
    transition: all 0.3s ease;

    &:focus {
      border-color: $brand-red;
      box-shadow: 0 0 0 0.2rem rgba(232, 0, 0, 0.15);
      outline: none;
    }

    &[readonly] {
      background-color: #f8f9fa;
      border-color: #dee2e6;
      color: lighten($brand-black, 30%);
    }
  }

  // Enhanced Select Styling to match brand
  .brand-select {
    .vs__dropdown-toggle {
      border: 2px solid #e3e6f0;
      border-radius: 8px;
      padding: 0.5rem 0.75rem;
      min-height: 48px;
      font-family: 'Poppins', sans-serif;
      background-color: #fff;
      transition: all 0.3s ease;

      &:hover {
        border-color: lighten($brand-red, 20%);
      }
    }

    .vs__selected {
      color: $brand-black;
      font-weight: 500;
      font-family: 'Poppins', sans-serif;
    }

    .vs__dropdown-menu {
      border: 2px solid rgba(232, 0, 0, 0.1);
      border-radius: 8px;
      box-shadow: 0 4px 25px 0 rgba(29, 29, 27, 0.15);
      font-family: 'Poppins', sans-serif;
    }

    .vs__dropdown-option--highlight {
      background-color: rgba(232, 0, 0, 0.1);
      color: $brand-black;
    }

    .vs__search {
      font-family: 'Poppins', sans-serif;
    }

    .vs__clear {
      color: lighten($brand-black, 40%);

      &:hover {
        color: $brand-red;
      }
    }

    .vs__open-indicator {
      color: lighten($brand-black, 40%);
    }

    &.vs--open .vs__dropdown-toggle {
      border-color: $brand-red;
      box-shadow: 0 0 0 0.2rem rgba(232, 0, 0, 0.15);
    }
  }

  // Button styling
  .brand-btn-primary {
    background: linear-gradient(135deg, $brand-red 0%, darken($brand-red, 10%) 100%);
    border: none;
    border-radius: 8px;
    padding: 0.75rem 2rem;
    font-weight: 500;
    font-family: 'Poppins', sans-serif;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    transition: all 0.3s ease;
    box-shadow: 0 4px 15px 0 rgba(232, 0, 0, 0.3);

    &:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 6px 20px 0 rgba(232, 0, 0, 0.4);
      background: linear-gradient(135deg, darken($brand-red, 5%) 0%, darken($brand-red, 15%) 100%);
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
      transform: none;
    }
  }

  .brand-btn-secondary {
    border: 2px solid $brand-black;
    color: $brand-black;
    background: transparent;
    border-radius: 8px;
    padding: 0.75rem 2rem;
    font-weight: 500;
    font-family: 'Poppins', sans-serif;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    transition: all 0.3s ease;

    &:hover:not(:disabled) {
      background: $brand-black;
      color: white;
      transform: translateY(-2px);
      box-shadow: 0 4px 15px 0 rgba(29, 29, 27, 0.3);
    }
  }

  // Table styling
  .vue-good-table {
    .font-weight-medium {
      font-weight: 500;
      color: $brand-black;
      font-family: 'Poppins', sans-serif;
    }

    .font-weight-bold {
      font-weight: 600;
      color: $brand-black;
      font-family: 'Poppins', sans-serif;
    }

    th {
      background-color: rgba(29, 29, 27, 0.03);
      color: $brand-black;
      font-family: 'Poppins', sans-serif;
      font-weight: 600;
    }
  }

  // Badge styling
  .brand-badge-large {
    font-size: 0.875rem;
    padding: 0.5rem 1rem;
    border-radius: 8px;
    font-weight: 500;
    font-family: 'Poppins', sans-serif;
  }

  // Modal styling
  .modal-header {
    background: rgba(29, 29, 27, 0.02);
    border-bottom: 2px solid rgba(232, 0, 0, 0.1);
  }

  .modal-footer {
    border-top: 2px solid rgba(232, 0, 0, 0.1);
  }

  // Enhanced datetime input styling
  .brand-datetime-input {
    border: 2px solid #e3e6f0;
    border-radius: 8px;
    padding: 0.75rem 1rem;
    font-size: 0.95rem;
    font-family: 'Poppins', sans-serif;
    transition: all 0.3s ease;
    background-color: #fff;
    color: $brand-black;

    &:focus {
      border-color: $brand-red;
      box-shadow: 0 0 0 0.2rem rgba(232, 0, 0, 0.15);
      outline: none;
    }

    &:disabled {
      background-color: #f8f9fa;
      border-color: #dee2e6;
      color: lighten($brand-black, 30%);
      cursor: not-allowed;
    }

    // Custom datetime picker styling for webkit browsers
    &::-webkit-calendar-picker-indicator {
      background-image: url('data:image/svg+xml;charset=UTF-8,<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="%23E80000" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>');
      background-repeat: no-repeat;
      background-position: center;
      background-size: 16px;
      width: 20px;
      height: 20px;
      margin-left: 4px;
      cursor: pointer;

      &:hover {
        background-image: url('data:image/svg+xml;charset=UTF-8,<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="%23B71C1C" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>');
      }
    }

    // Firefox datetime styling
    &::-moz-placeholder {
      color: #6c757d;
      opacity: 1;
    }

    // Edge/IE datetime styling
    &::-ms-input-placeholder {
      color: #6c757d;
    }

    // Mobile responsive adjustments
    @media (max-width: 768px) {
      font-size: 16px; // Prevent zoom on iOS
      padding: 0.875rem 1rem; // Slightly larger touch target
    }
  }
}

// Ensure styles are loaded immediately
body, html {
  font-family: 'Poppins', sans-serif !important;
}

// Force vue-select and vue-good-table to inherit font
.vs__dropdown-toggle,
.vs__selected,
.vs__search,
.vs__dropdown-menu,
.vgt-table,
.vgt-table th,
.vgt-table td {
  font-family: 'Poppins', sans-serif !important;
}
</style>
